import { createSelector } from 'reselect';
import { getElementTransformAtFrame } from 'content-creation-common/utils/transform';

import { PLAYBACK_FRAMES_PER_SECOND } from '../constants';
import { DEFAULT_KEYFRAME_TRANSFORM } from '../features/creative/constants';
import { isNil, isNotNil } from '../util/object';
import {
  getById as getElementById,
  getRange as getElementRange,
} from './element';
import { getTotalFrames } from '../features/shared/playback/state-management/selectors';
import { getCanvasRelativeElementTransformAtFrame } from '../util/transform';
import { getKeyFramesInElement, getNearestKeyFrames } from '../util/keyframe';
import { getCurrentFrame } from '../features/playback/manager/PlaybackManager';

// This helps avoid a circular dependency issue.
const getById = (state, elementId) => {
  return getElementById(state, elementId);
};

// This helps avoid a circular dependency issue.
const getRange = (state, elementId) => {
  return getElementRange(state, elementId);
};

export const isFrameInSet = (element, frame) => {
  const { transforms } = element;
  const transitionKeys = Object.keys(transforms).map((frame) =>
    parseInt(frame, 10),
  );
  const { leftKeyFrame, rightKeyFrame } = getNearestKeyFrames(
    transitionKeys,
    frame,
  );
  return (
    isNotNil(rightKeyFrame) &&
    isNotNil(leftKeyFrame) &&
    transforms[leftKeyFrame].setId &&
    transforms[leftKeyFrame].setId === transforms[rightKeyFrame].setId
  );
};

export const findNextKeyFrameInsertionPoint = (
  currentFrame,
  element,
  desiredDuration = 0,
  totalFrames,
) => {
  const { transforms } = element;
  const transformKeys = Object.keys(transforms).map((key) => parseInt(key, 10));
  const { leftKeyFrame } = getNearestKeyFrames(transformKeys, currentFrame);

  let startFrame = Math.max(
    0,
    currentFrame - desiredDuration,
    leftKeyFrame + 1,
  );
  let looking = true;

  while (looking) {
    if (transforms.hasOwnProperty(startFrame.toString())) {
      startFrame = startFrame + 1;
    } else {
      if (isFrameInSet(element, startFrame)) {
        const { rightKeyFrame } = getNearestKeyFrames(
          transformKeys,
          startFrame,
        );
        startFrame = rightKeyFrame + 1;
      } else {
        looking = false;
      }
    }
  }

  const { rightKeyFrame: nextKeyFrame } = getNearestKeyFrames(
    transformKeys,
    startFrame,
  );

  let duration = currentFrame - startFrame;
  if (duration <= 0) {
    const nextKeyframeDuration = isNil(nextKeyFrame)
      ? Infinity
      : nextKeyFrame - 1 - startFrame;
    duration = Math.min(
      PLAYBACK_FRAMES_PER_SECOND / 2,
      totalFrames - startFrame,
      nextKeyframeDuration,
    );
  }

  return {
    duration,
    startFrame,
  };
};

export const getAnimationKeyFrameSet = (element, setId) => {
  const keys = Object.keys(element.transforms).sort((a, b) => {
    return a - b;
  });
  return keys.filter((frameKey) => {
    return element.transforms[frameKey].setId === setId;
  });
};

export const getAnimationKeyFrames = (state, elementId) => {
  const element = getById(state, elementId);
  if (!element) {
    throw new Error(`could not find element with id ${elementId}`);
  }
  return getKeyFramesInElement(element);
};

const getElementTransformAtFrameWithParentTransforms = (
  state,
  elementId,
  frame,
  useCurrent,
) => {
  const elementGetter = (givenElementId) => getById(state, givenElementId);
  return getCanvasRelativeElementTransformAtFrame(
    elementId,
    frame,
    useCurrent,
    elementGetter,
  );
};

export const getElementTransformWithParentTransformsAtCurrentFrame = (
  state,
  elementId,
) => {
  const currentFrame = getCurrentFrame();
  return getElementTransformAtFrameWithParentTransforms(
    state,
    elementId,
    currentFrame,
    false,
  );
};

export const getElementTransformWithParentTransformsAtFrame = (
  state,
  elementId,
  frameNum,
) => {
  return getElementTransformAtFrameWithParentTransforms(
    state,
    elementId,
    frameNum,
    false,
  );
};

export const getTransformForCurrentFrame = createSelector(
  [getCurrentFrame, getById],
  (currentFrame, element) => {
    if (element) {
      return getElementTransformAtFrame(element, currentFrame);
    }

    return DEFAULT_KEYFRAME_TRANSFORM;
  },
);

export const getTransformForFrame = createSelector(
  [getById, (state, elId, frameNumber) => frameNumber],
  (element, frameNumber) => {
    if (!element) {
      return DEFAULT_KEYFRAME_TRANSFORM;
    }

    return getElementTransformAtFrame(element, frameNumber);
  },
);

export const getElementKeyframesInRange = createSelector(
  [getById, getRange, getAnimationKeyFrames, getTotalFrames],
  (element, range, animationKeyframes, totalFrames) => {
    /*
      Return keyframes between 0 and Total Frames that are also within range of the element
      and that do not have set ID or are the first keyframe in a set
     */
    return animationKeyframes.filter((keyframe) => {
      return (
        keyframe.frame >= 0 &&
        keyframe.frame < totalFrames &&
        (keyframe.frame >= range.start || keyframe.frame <= range.end) &&
        (!keyframe.setId || keyframe.setOrder === 0)
      );
    });
  },
);
